import React, { useState } from "react";
import { useSelector } from "react-redux";
import { faArrowsRotate, faBan, faCopy, faKey } from "@fortawesome/pro-regular-svg-icons";
import { Alert, Badge, Input, InputGroup, Label, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";

import useConfig from "../../../../actions/Tenants/config/configHook";
import { UserRole } from "../../../../actions/Tenants/config/constantsTyped";
import useAuthorization from "../../../../utils/authorization";
import { format } from "../../../../utils/date";
import { toast } from "../../../../utils/toast";
import { Button } from "../../../Helper/Button";
import { Loading } from "../../../Helper/Loading";
import { ConfirmationModal } from "../../Helper/ConfirmationModal";
import { ModalResult } from "../../Helper/Modal";
import { useRevokeScimToken, useRotateScimToken, useScimStatus } from "./scimProvisioningHooks";

const copyToClipboard = async (value: string, what: string) => {
    try {
        await navigator.clipboard.writeText(value);
        toast.info(`${what} copied to clipboard`);
    } catch {
        toast.warning("Failed to copy to clipboard");
    }
};

const CopyField = ({ id, label, value }: { id: string; label: string; value: string }) => (
    <div className="mb-3">
        <Label for={id} className="fw-bold">{label}</Label>
        <InputGroup>
            <Input id={id} readOnly value={value} className="font-monospace" onFocus={(e) => e.target.select()} />
            <Button color="light" icon={faCopy} aria-label={`Copy ${label}`} onClick={() => copyToClipboard(value, label)} />
        </InputGroup>
    </div>
);

type PendingAction = "rotate" | "revoke" | null;

export const ScimProvisioning: React.FC = () => {
    const user = useSelector((state: any) => state.auth.user);
    const auth = useAuthorization();
    const tenantId: string = useConfig()?.tenant_id;
    const canManage = user?.is_admin || auth.userHasRole(UserRole.TENANT_ADMIN);

    const { data: status, isLoading, isError } = useScimStatus(canManage ? tenantId : "");
    const rotate = useRotateScimToken(tenantId);
    const revoke = useRevokeScimToken(tenantId);
    const [pending, setPending] = useState<PendingAction>(null);
    const [newToken, setNewToken] = useState<string | null>(null);

    if (!canManage) {
        return <Alert color="warning">This page is available to tenant admins only.</Alert>;
    }

    const generate = () => rotate.mutate(undefined, { onSuccess: (data) => setNewToken(data.token) });

    const onConfirm = (result: ModalResult) => {
        const action = pending;
        setPending(null);
        if (result !== ModalResult.Save) return;
        if (action === "rotate") generate();
        if (action === "revoke") revoke.mutate(undefined, { onSuccess: () => toast.success("SCIM token revoked") });
    };

    return (
        <div className="p-3" style={{ maxWidth: 760 }}>
            <h4 className="mb-1">SCIM provisioning</h4>
            <p className="text-muted small">
                Let your Microsoft Entra ID add and remove the users who can open this tenant.
                Users still sign in with their own Entra account; roles stay managed on the Users page.
            </p>

            {isLoading && <Loading />}

            {isError && <Alert color="danger">Could not load the SCIM provisioning status. Reload the page to try again.</Alert>}

            {status && !status.available && (
                <Alert color="info">
                    {status.unavailable_reason === "experience_data_tenant"
                        ? "SCIM provisioning is not available for the Experience Data tenant. Switch to a customer tenant to set it up."
                        : "SCIM provisioning is not enabled for this tenant. Contact Experience Data to enable it."}
                </Alert>
            )}

            {status && !status.available && status.enabled && (
                // A token from before SCIM was turned off; the ingest service already refuses it, but let admins clean it up
                <Button color="danger" outline icon={faBan} isLoading={revoke.isPending} onClick={() => setPending("revoke")}>
                    Revoke token
                </Button>
            )}

            {status?.available && (
                <>
                    <p>
                        Status:{" "}
                        {status.enabled ? (
                            <Badge color="success">Active since {format(status.created)}</Badge>
                        ) : (
                            <Badge color="secondary">Not set up</Badge>
                        )}
                    </p>

                    {status.tenant_url ? (
                        <CopyField id="scim-tenant-url" label="Tenant URL" value={status.tenant_url} />
                    ) : (
                        <Alert color="warning">
                            The Tenant URL is not configured for this environment yet. Contact Experience Data before setting up Entra.
                        </Alert>
                    )}

                    <div className="d-flex gap-2">
                        {status.enabled ? (
                            <>
                                <Button color="primary" icon={faArrowsRotate} isLoading={rotate.isPending} onClick={() => setPending("rotate")}>
                                    Generate new token
                                </Button>
                                <Button color="danger" outline icon={faBan} isLoading={revoke.isPending} onClick={() => setPending("revoke")}>
                                    Revoke token
                                </Button>
                            </>
                        ) : (
                            <Button color="primary" icon={faKey} isLoading={rotate.isPending} onClick={generate}>
                                Generate token
                            </Button>
                        )}
                    </div>

                    <p className="text-muted small mt-3 mb-0">
                        In Entra, open the enterprise application, go to Provisioning and paste the Tenant URL and the token as the Secret Token.
                    </p>
                </>
            )}

            <ConfirmationModal size="md" isOpen={pending !== null} onClose={onConfirm}>
                {pending === "rotate"
                    ? "The current token stops working immediately. Provisioning fails until the new token is entered in Entra. Continue?"
                    : "Provisioning from Entra stops until a new token is generated. Existing users keep their access. Continue?"}
            </ConfirmationModal>

            <Modal isOpen={newToken !== null} size="lg" backdrop="static">
                <ModalHeader>SCIM token</ModalHeader>
                <ModalBody>
                    <Alert color="warning">Copy this token now. It is shown only once and cannot be retrieved later.</Alert>
                    <CopyField id="scim-token" label="Secret Token" value={newToken ?? ""} />
                </ModalBody>
                <ModalFooter>
                    <Button color="primary" onClick={() => setNewToken(null)}>I have copied the token</Button>
                </ModalFooter>
            </Modal>
        </div>
    );
};

export default ScimProvisioning;
